'use client';

import { useEffect, useSyncExternalStore } from 'react';
import { GlassBar } from '@/lib/native/glass-bar';
import { hasNativePlugin, isNative } from '@/lib/native/is-native';

/**
 * Full-screen surfaces that cover the whole app, so the Mucka Bar must go.
 *
 * Same shape as `intro-surface.ts` and `offline-surface.ts`, for the surfaces
 * that are neither: the Face ID lock (`AppLock`) and the "Lock with Face ID"
 * offer after sign-in (`BiometricOnboarding`). Both live in `AuthedShell`;
 * `MobileNav` lives in the ROOT layout and cannot see them, so without this the
 * bar sat on top of the lock screen. On the native glass bar that is worse
 * than on the web: it is a UIKit / Android view ABOVE the WebView, so no
 * z-index on the page can ever cover it (Tom, 2026-10-11).
 *
 * 🚨 The hook alone is too late for the Face ID lock (Tom, 2026-10-16: the bar
 * still over "Alright Tom, is that you?"). `useCoverChrome` runs in an effect,
 * AFTER the lock screen has been committed and painted, and the hide it causes
 * needs a second render of MobileNav before it reaches native. So the lock was
 * always drawn under the bar first — and on a return from the background the
 * bar had been up since the moment iOS showed the app, long before any of our
 * JS ran. `coverChromeNow` and `hideNativeBarNow` are for the moment a surface
 * DECIDES to cover, from a native lifecycle event, before React draws anything.
 */

const covers = new Set<string>();
const listeners = new Set<() => void>();

function emit(): void {
  for (const fn of listeners) fn();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

const getSnapshot = (): boolean => covers.size > 0;
// The surfaces decide on the client, so the server never hides the bar.
const getServerSnapshot = (): boolean => false;

/** While `active`, this surface covers the app and the bar stands down. */
export function useCoverChrome(id: string, active: boolean): void {
  useEffect(() => {
    if (!active) return;
    covers.add(id);
    emit();
    return () => {
      if (covers.delete(id)) emit();
    };
  }, [id, active]);
}

/** True while any full-screen surface covers the app. Read by the chrome. */
export function useChromeCovered(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * Tell the native glass bar to go, in this tick — no render in between. A
 * no-op on the web and on a build without the plugin.
 */
export function hideNativeBarNow(): void {
  if (!isNative() || !hasNativePlugin('MuckaGlassBar')) return;
  GlassBar.setMode({ mode: 'hidden', animated: false }).catch(() => {});
}

/**
 * Cover the app now, from outside a render: the bar is told to go in this
 * tick, and MobileNav stands its bar down on its next render. The surface's
 * own `useCoverChrome(id, …)` takes over from here and removes the cover when
 * it goes, so pass the same id.
 */
export function coverChromeNow(id: string): void {
  hideNativeBarNow();
  if (covers.has(id)) return;
  covers.add(id);
  emit();
}

// Put away while the app is in the background with the lock on. 🚨 Until it
// is released NOTHING may show the bar: a voice call's transcript opening the
// composer, a fresh NativeGlassBar mount, any mode change — each would put it
// back over the screen iOS shows on return, before the lock has decided.
let away = false;

/** True while the bar is put away and nothing may draw it. */
export function nativeBarAway(): boolean {
  return away;
}

/**
 * True while nothing may draw the native bar: it is put away, or a surface
 * covers the app. The second matters on a cold start that locks: the lock
 * covers in its mount effect, and a NativeGlassBar mounted in that same first
 * render would otherwise configure (and so show) the bar a moment later.
 */
export function nativeBarHeld(): boolean {
  return away || covers.size > 0;
}

/** The app is going to the background with the lock on: bar away, and kept away. */
export function putNativeBarAway(): void {
  away = true;
  hideNativeBarNow();
}

// Whatever native bar is on screen, and how to put it back as it was. Set by
// NativeGlassBar while it is mounted; nothing to put back when it is not.
let restore: (() => void) | null = null;

/** NativeGlassBar registers how to redraw itself after `putNativeBarAway`. */
export function onNativeBarRestore(fn: () => void): () => void {
  restore = fn;
  return () => { if (restore === fn) restore = null; };
}

/**
 * Release the bar: the return did not lock, or the lock has just been
 * unlocked. Redraws the mounted bar as it is now; with none mounted (the lock
 * covers it) the next mount draws itself.
 */
export function restoreNativeBar(): void {
  away = false;
  restore?.();
}
